import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import MarketingShell from "@/components/MarketingShell";
import SentenceLines from "@/components/SentenceLines";
import JmlLink from "@/components/JmlLink";
import Staff from "@/components/Staff";
import { MAJOR_INTERVALS } from "@/lib/hebrewCryptogram";
import { buildHebrewSequence } from "@/lib/hebrewComposition";
import { playSequenceAudio } from "@/lib/playEngine";
import { unlockAudio } from "@/lib/synth";
import { logDebug } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.4, 0, 0.2, 1];

// Same key the /try-it demo is locked to, so the podcast staff sounds familiar.
const SCALE = { root: "G", modeIdx: 0, intervals: MAJOR_INTERVALS, baseOctave: 2 };

const goldCta =
  "inline-flex items-center justify-center whitespace-nowrap rounded-full px-7 h-12 text-xs uppercase transition-colors hover:bg-[#F2D589]/[0.12] disabled:opacity-40";
const goldStyle = {
  backgroundColor: "transparent",
  color: "#F2D589",
  border: "1px solid #F2D589",
  letterSpacing: "0.18em",
  fontWeight: 500,
};
const plainCta =
  "inline-flex items-center justify-center whitespace-nowrap rounded-full px-6 h-12 text-xs uppercase transition-colors hover:border-[#FCA07A] hover:text-[#FFD9C2]";
const plainStyle = {
  border: "1px solid #241246",
  color: "#C6BBA9",
  letterSpacing: "0.18em",
  fontWeight: 500,
};

export function displayDate(ep) {
  if (!ep) return "";
  if (ep.date) return ep.date;
  if (!ep.publish_at) return "";
  const [y, m, d] = ep.publish_at.split("-").map(Number);
  if (!y || !m || !d) return ep.publish_at;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

export function artworkUrl(ep) {
  if (!ep) return "";
  const uploaded = ep.artwork_url || "";
  if (uploaded) {
    return uploaded.startsWith("http") ? uploaded : `${process.env.REACT_APP_BACKEND_URL}${uploaded}`;
  }
  // Nothing uploaded? YouTube already made us a thumbnail.
  return ep.video_id ? `https://img.youtube.com/vi/${ep.video_id}/maxresdefault.jpg` : "";
}

export default function PodcastEpisode() {
  const { slug } = useParams();
  const [episodes, setEpisodes] = useState(null);
  const [dropped, setDropped] = useState([]);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const stopRef = useRef(false);

  useEffect(() => {
    axios
      .get(`${API}/podcast/episodes`)
      .then(({ data }) => setEpisodes(data.episodes || []))
      .catch((err) => {
        logDebug("episodes unavailable", err);
        setEpisodes([]);
      });
  }, []);

  const idx = useMemo(
    () => (episodes || []).findIndex((e) => e.slug === slug),
    [episodes, slug]
  );
  const ep = idx >= 0 ? episodes[idx] : null;
  // The list is newest-first, so the *next* episode sits at a lower index.
  const newer = idx > 0 ? episodes[idx - 1] : null;
  const older = episodes && idx >= 0 && idx < episodes.length - 1 ? episodes[idx + 1] : null;

  const words = useMemo(() => {
    const he = (ep?.parsha_he || "").trim();
    return he ? he.split(/\s+/).filter(Boolean) : [];
  }, [ep]);
  const sequence = useMemo(() => buildHebrewSequence(words, SCALE), [words]);

  const play = useCallback(async () => {
    if (playing || !sequence.length) return;
    unlockAudio();
    stopRef.current = false;
    setPlaying(true);
    setDropped([]);
    const collected = [];
    await playSequenceAudio(sequence, {
      presetKey: "meditation",
      voice: "pad",
      pedal: false,
      onTick: (i, entry) => {
        if (stopRef.current) return;
        collected.push(entry);
        setDropped([...collected]);
        setActiveIdx(i);
      },
    });
    setActiveIdx(-1);
    setPlaying(false);
  }, [playing, sequence]);

  // Overwrite the site-wide social card with this episode's, then put it back.
  // Editing the existing tags (rather than adding new ones) keeps exactly one
  // og:image in <head>, which is what crawlers expect.
  useEffect(() => {
    if (!ep) return undefined;
    const desc = ep.blurb || `${ep.parsha}, played.`;
    const heading = `Episode ${ep.number} — ${ep.parsha} | Alephonics`;
    const art = artworkUrl(ep);
    const tags = [
      ["name", "description", desc],
      ["property", "og:type", "article"],
      ["property", "og:title", heading],
      ["property", "og:description", desc],
      ["property", "og:url", `${window.location.origin}/podcast/${ep.slug}`],
      ["name", "twitter:title", heading],
      ["name", "twitter:description", desc],
      ...(art
        ? [
            ["property", "og:image", art],
            ["name", "twitter:image", art],
          ]
        : []),
    ];
    const prevTitle = document.title;
    document.title = heading;
    const restore = tags.map(([attr, key, value]) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      const created = !el;
      if (created) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      const prev = created ? null : el.getAttribute("content");
      el.setAttribute("content", value);
      return () => (created ? el.remove() : el.setAttribute("content", prev));
    });
    return () => {
      document.title = prevTitle;
      restore.forEach((undo) => undo());
    };
  }, [ep]);

  const copyLink = async () => {
    const url = `${window.location.origin}/podcast/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied.");
    } catch (err) {
      toast.message(url);
    }
  };

  if (episodes === null) {
    return (
      <MarketingShell active="/podcast">
        <p className="mt-10 text-sm" style={{ color: "#C6BBA9" }} data-testid="episode-loading">
          One moment…
        </p>
      </MarketingShell>
    );
  }

  if (!ep) {
    return (
      <MarketingShell active="/podcast">
        <Toaster position="bottom-right" richColors />
        <div className="mt-8" data-testid="episode-not-found">
          <p className="text-[11px] uppercase mb-4" style={{ color: "#F2D589", letterSpacing: "0.28em" }}>
            Episode {slug}
          </p>
          <h1
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 300,
              fontSize: "var(--step-3)",
              lineHeight: 1.12,
              maxWidth: "26ch",
            }}
          >
            <SentenceLines text="This one isn't out yet." />
          </h1>
          <p className="mt-8 max-w-[62ch] text-base sm:text-lg" style={{ color: "#C6BBA9", lineHeight: 1.7 }}>
            Either it&apos;s still scheduled or the number doesn&apos;t exist. The archive has
            everything that&apos;s live.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/podcast" className={goldCta} style={goldStyle} data-testid="episode-back-archive">
              Back to the archive
            </Link>
            <JmlLink className={plainCta} style={plainStyle} testId="episode-404-tryit">
              Hear a word or verse
            </JmlLink>
          </div>
        </div>
      </MarketingShell>
    );
  }

  return (
    <MarketingShell active="/podcast">
      <Toaster position="bottom-right" richColors />
      <motion.article
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE }}
        data-testid="episode-page"
      >

        <Link
          to="/podcast"
          className="text-[11px] uppercase"
          style={{ color: "#FCA07A", letterSpacing: "0.24em" }}
          data-testid="episode-breadcrumb"
        >
          &larr; The Podcast
        </Link>

        <p className="mt-8 text-[11px] uppercase" style={{ color: "#F2D589", letterSpacing: "0.28em" }}>
          Episode {ep.number}
          {displayDate(ep) ? ` · ${displayDate(ep)}` : ""}
        </p>
        <h1
          className="mt-4"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 300,
            fontSize: "var(--step-3)",
            letterSpacing: "-0.02em",
            lineHeight: 1.12,
          }}
          data-testid="episode-title"
        >
          {ep.parsha}{" "}
          {ep.parsha_he && (
            <span dir="rtl" style={{ fontFamily: "'Frank Ruhl Libre', serif", color: "#F2D589" }}>
              {ep.parsha_he}
            </span>
          )}
        </h1>

        {ep.blurb && (
          <p className="mt-8 max-w-[68ch] text-base sm:text-lg" style={{ color: "#C6BBA9", lineHeight: 1.7 }} data-testid="episode-blurb">
            {ep.blurb}
          </p>
        )}

        {ep.video_id && (
          <div
            className="mt-12 w-full"
            style={{ aspectRatio: "16 / 9", backgroundColor: "#0C0826", border: "1px solid #241246" }}
            data-testid="episode-video"
          >
            <iframe
              title={`Episode ${ep.number} — ${ep.parsha}`}
              src={`https://www.youtube.com/embed/${ep.video_id}`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
              allowFullScreen
              className="h-full w-full"
              style={{ border: 0 }}
            />
          </div>
        )}

        {sequence.length > 0 && (
          <section className="mt-20" data-testid="episode-staff-section">
            <p className="text-[11px] uppercase" style={{ color: "#F2D589", letterSpacing: "0.24em" }}>
              The name, as notes
            </p>
            <h2
              className="mt-4"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-2)" }}
            >
              Hear {ep.parsha}
            </h2>
            <div className="mt-8">
              <Staff droppedEntries={dropped} activeIndex={activeIdx} baseStepMs={800} showNoteNames={false} />
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <button
                type="button"
                onClick={play}
                disabled={playing}
                className={goldCta}
                style={goldStyle}
                data-testid="episode-play"
              >
                {playing ? "Playing" : "Play the name"}
              </button>
              <button type="button" onClick={copyLink} className={plainCta} style={plainStyle} data-testid="episode-copy-link">
                Copy link
              </button>
            </div>
          </section>
        )}

        {sequence.length === 0 && (
          <div className="mt-12">
            <button type="button" onClick={copyLink} className={plainCta} style={plainStyle} data-testid="episode-copy-link">
              Copy link
            </button>
          </div>
        )}

        {/* ── prev / next ── */}
        <nav
          className="mt-24 flex flex-wrap items-stretch justify-between gap-6"
          style={{ borderTop: "1px solid #241246", paddingTop: "2rem" }}
          data-testid="episode-nav"
        >
          {older ? (
            <Link to={`/podcast/${older.slug}`} className="max-w-[26ch]" data-testid="episode-prev">
              <p className="text-[11px] uppercase" style={{ color: "#C6BBA9", letterSpacing: "0.2em" }}>
                &larr; Episode {older.number}
              </p>
              <p className="mt-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.35rem", fontWeight: 300 }}>
                {older.parsha}
              </p>
            </Link>
          ) : (
            <span />
          )}
          {newer ? (
            <Link to={`/podcast/${newer.slug}`} className="max-w-[26ch] text-right" data-testid="episode-next">
              <p className="text-[11px] uppercase" style={{ color: "#C6BBA9", letterSpacing: "0.2em" }}>
                Episode {newer.number} &rarr;
              </p>
              <p className="mt-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.35rem", fontWeight: 300 }}>
                {newer.parsha}
              </p>
            </Link>
          ) : (
            <span />
          )}
        </nav>

        {/* ── asks ── */}
        <section className="mt-24" data-testid="episode-ctas">
          <h2
            className="max-w-[34ch]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: "var(--step-2)" }}
          >
            <SentenceLines text="Now put your own word through it." />
          </h2>
          <div className="mt-10 flex flex-wrap gap-4">
            <JmlLink className={goldCta} style={goldStyle} testId="episode-cta-tryit">
              Hear a word or verse
            </JmlLink>
            <Link to="/patrons" className={plainCta} style={plainStyle} data-testid="episode-cta-patrons">
              Support the music
            </Link>
          </div>
        </section>
      </motion.article>
    </MarketingShell>
  );
}
